// Medical Scores Database - Complete Collection Part 3
// Scores H-M from medicalcul.free.fr

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Users,
  Zap,
  Target,
  Shield
} from 'lucide-react';

export const SCORES_PART_3 = {
  // Hendrich II - Risque de chute
  hendrich: {
    name: 'Échelle Hendrich II',
    category: 'Gériatrie',
    icon: User,
    description: 'Risque de chute hospitalisé',
    color: 'amber',
    fields: [
      { id: 'confusion', label: 'Confusion/Désorientation', type: 'checkbox', points: 4 },
      { id: 'depression', label: 'Dépression symptomatique', type: 'checkbox', points: 2 },
      { id: 'vertigo', label: 'Vertiges', type: 'checkbox', points: 1 },
      { id: 'maleGender', label: 'Sexe masculin', type: 'checkbox', points: 1 },
      { id: 'antiepileptics', label: 'Antiépileptiques', type: 'checkbox', points: 2 },
      { id: 'benzodiazepines', label: 'Benzodiazépines', type: 'checkbox', points: 1 },
      { id: 'getUpGo', label: 'Test "Get up and go" anormal', type: 'checkbox', points: 4 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.confusion) total += 4;
      if (values.depression) total += 2;
      if (values.vertigo) total += 1;
      if (values.maleGender) total += 1;
      if (values.antiepileptics) total += 2;
      if (values.benzodiazepines) total += 1;
      if (values.getUpGo) total += 4;
      return { score: total, max: 15 };
    },
    interpret: (score) => {
      if (score < 5) return { level: 'success', text: `Hendrich ${score}`, detail: 'Risque faible de chute' };
      return { level: 'danger', text: `Hendrich ${score}`, detail: 'Risque élevé de chute - Mesures préventives' };
    },
    formula: 'Hendrich II ≥ 5 = risque élevé de chute'
  },

  // HScore - Syndrome hémophagocytaire
  hscore: {
    name: 'HScore',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Probabilité syndrome hémophagocytaire',
    color: 'red',
    fields: [
      { id: 'immunosuppression', label: 'Immunosuppression connue (VIH, chimio)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 18, label: 'Oui (18 pts)' }
      ]},
      { id: 'temperature', label: 'Température', type: 'select', options: [
        { value: 0, label: '< 38.4°C (0 pt)' },
        { value: 33, label: '38.4-39.4°C (33 pts)' },
        { value: 49, label: '> 39.4°C (49 pts)' }
      ]},
      { id: 'organomegaly', label: 'Organomégalie', type: 'select', options: [
        { value: 0, label: 'Aucune (0 pt)' },
        { value: 23, label: 'Hépato ou splénomégalie (23 pts)' },
        { value: 38, label: 'Hépatosplénomégalie (38 pts)' }
      ]},
      { id: 'cytopenia', label: 'Nombre de cytopénies', type: 'select', options: [
        { value: 0, label: '1 lignée (0 pt)' },
        { value: 24, label: '2 lignées (24 pts)' },
        { value: 34, label: '3 lignées (34 pts)' }
      ]},
      { id: 'ferritin', label: 'Ferritine (µg/L)', type: 'select', options: [
        { value: 0, label: '< 2000 (0 pt)' },
        { value: 35, label: '2000-6000 (35 pts)' },
        { value: 50, label: '> 6000 (50 pts)' }
      ]},
      { id: 'triglycerides', label: 'Triglycérides (mmol/L)', type: 'select', options: [
        { value: 0, label: '< 1.5 (0 pt)' },
        { value: 44, label: '1.5-4 (44 pts)' },
        { value: 64, label: '> 4 (64 pts)' }
      ]},
      { id: 'fibrinogen', label: 'Fibrinogène (g/L)', type: 'select', options: [
        { value: 0, label: '> 2.5 (0 pt)' },
        { value: 30, label: '≤ 2.5 (30 pts)' }
      ]},
      { id: 'ast', label: 'ASAT', type: 'select', options: [
        { value: 0, label: '< 30 UI/L (0 pt)' },
        { value: 19, label: '≥ 30 UI/L (19 pts)' }
      ]},
      { id: 'hemophagocytosis', label: 'Hémophagocytose myélogramme', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 35, label: 'Oui (35 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      // Calculate probability using logistic regression
      const prob = 1 / (1 + Math.exp(-(total - 168) / 40));
      return { score: total, max: 337, probability: (prob * 100).toFixed(0) };
    },
    interpret: (score, calculated) => {
      const prob = calculated?.probability || 0;
      if (score < 90) return { level: 'success', text: `HScore ${score}`, detail: `Probabilité SH < 1%` };
      if (score < 169) return { level: 'warning', text: `HScore ${score}`, detail: `Probabilité SH ~${prob}%` };
      return { level: 'danger', text: `HScore ${score}`, detail: `Probabilité SH ~${prob}% - Recherche étiologique urgente` };
    },
    formula: 'HScore > 169 = probabilité SH > 93%'
  },

  // IVIG - International Prognostic Index (IPI) Lymphome
  ipi: {
    name: 'Score IPI',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Pronostic lymphome agressif',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge > 60 ans', type: 'checkbox', points: 1 },
      { id: 'ldh', label: 'LDH > normale', type: 'checkbox', points: 1 },
      { id: 'ecog', label: 'ECOG ≥ 2', type: 'checkbox', points: 1 },
      { id: 'stage', label: 'Stade III ou IV', type: 'checkbox', points: 1 },
      { id: 'extranodal', label: '> 1 site extra-nodal', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `IPI ${score}`, detail: 'Faible risque - Survie 5 ans ~73%' };
      if (score === 2) return { level: 'warning', text: `IPI ${score}`, detail: 'Risque faible-intermédiaire - Survie 5 ans ~51%' };
      if (score === 3) return { level: 'warning', text: `IPI ${score}`, detail: 'Risque élevé-intermédiaire - Survie 5 ans ~43%' };
      return { level: 'danger', text: `IPI ${score}`, detail: 'Haut risque - Survie 5 ans ~26%' };
    },
    formula: 'IPI = Âge>60 + LDH↑ + ECOG≥2 + StadeIII-IV + >1 extra-nodal'
  },

  // IPSS-R - Myelodysplasie
  ipssR: {
    name: 'Score IPSS-R',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Pronostic syndrome myélodysplasique',
    color: 'red',
    fields: [
      { id: 'cytogenetics', label: 'Cytogénétique', type: 'select', options: [
        { value: 0, label: 'Très bon (0 pt)' },
        { value: 1, label: 'Bon (1 pt)' },
        { value: 2, label: 'Intermédiaire (2 pts)' },
        { value: 3, label: 'Mauvais (3 pts)' },
        { value: 4, label: 'Très mauvais (4 pts)' }
      ]},
      { id: 'blasts', label: 'Blastes médullaires (%)', type: 'select', options: [
        { value: 0, label: '≤ 2% (0 pt)' },
        { value: 1, label: '> 2 - < 5% (1 pt)' },
        { value: 2, label: '5-10% (2 pts)' },
        { value: 3, label: '> 10% (3 pts)' }
      ]},
      { id: 'hemoglobin', label: 'Hémoglobine (g/dL)', type: 'select', options: [
        { value: 0, label: '≥ 10 (0 pt)' },
        { value: 1, label: '8 - <10 (1 pt)' },
        { value: 1.5, label: '< 8 (1.5 pts)' }
      ]},
      { id: 'platelets', label: 'Plaquettes (G/L)', type: 'select', options: [
        { value: 0, label: '≥ 100 (0 pt)' },
        { value: 0.5, label: '50 - <100 (0.5 pt)' },
        { value: 1, label: '< 50 (1 pt)' }
      ]},
      { id: 'anc', label: 'PNN (G/L)', type: 'select', options: [
        { value: 0, label: '≥ 0.8 (0 pt)' },
        { value: 0.5, label: '< 0.8 (0.5 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total.toFixed(1), max: 10 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s <= 1.5) return { level: 'success', text: `IPSS-R ${score}`, detail: 'Très faible risque - Survie médiane ~8.8 ans' };
      if (s <= 3) return { level: 'success', text: `IPSS-R ${score}`, detail: 'Faible risque - Survie médiane ~5.3 ans' };
      if (s <= 4.5) return { level: 'warning', text: `IPSS-R ${score}`, detail: 'Risque intermédiaire - Survie médiane ~3 ans' };
      if (s <= 6) return { level: 'danger', text: `IPSS-R ${score}`, detail: 'Haut risque - Survie médiane ~1.6 ans' };
      return { level: 'danger', text: `IPSS-R ${score}`, detail: 'Très haut risque - Survie médiane ~0.8 an' };
    },
    formula: 'IPSS-R = Cytogénétique + Blastes + Hb + PLT + PNN'
  },

  // Mayo - Cirrhose biliaire primitive
  mayoPBC: {
    name: 'Score de Mayo (CBP)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Pronostic cirrhose biliaire primitive',
    color: 'yellow',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'bilirubin', label: 'Bilirubine (mg/dL)', type: 'number', min: 0, max: 50, step: 0.1 },
      { id: 'albumin', label: 'Albumine (g/dL)', type: 'number', min: 1, max: 6, step: 0.1 },
      { id: 'pt', label: 'Temps de prothrombine (sec)', type: 'number', min: 8, max: 50 },
      { id: 'edema', label: 'Œdèmes', type: 'select', options: [
        { value: 0, label: 'Absents ou traités par diurétiques' },
        { value: 0.5, label: 'Présents malgré diurétiques' },
        { value: 1, label: 'Œdèmes réfractaires' }
      ]}
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const bil = values.bilirubin || 1;
      const alb = values.albumin || 4;
      const pt = values.pt || 12;
      const edema = values.edema || 0;
      
      const R = 0.871 * Math.log(bil) - 2.53 * Math.log(alb) + 0.039 * age + 2.38 * Math.log(pt) + 0.859 * edema;
      return { score: R.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const R = parseFloat(score);
      // Survival estimation
      const S0_1year = 0.970;
      const S0_2year = 0.941;
      const survival1 = Math.pow(S0_1year, Math.exp(R)) * 100;
      const survival2 = Math.pow(S0_2year, Math.exp(R)) * 100;
      
      if (R < 4.5) return { level: 'success', text: `Mayo R = ${score}`, detail: `Survie estimée: 1 an ~${survival1.toFixed(0)}%, 2 ans ~${survival2.toFixed(0)}%` };
      if (R < 6) return { level: 'warning', text: `Mayo R = ${score}`, detail: `Survie estimée: 1 an ~${survival1.toFixed(0)}%, 2 ans ~${survival2.toFixed(0)}%` };
      return { level: 'danger', text: `Mayo R = ${score}`, detail: `Survie estimée limitée - Transplantation à discuter` };
    },
    formula: 'Mayo = 0.871×ln(Bili) - 2.53×ln(Alb) + 0.039×Age + 2.38×ln(TP) + 0.859×Œdème'
  },

  // McMahon - Douleur thoracique
  mcmahon: {
    name: 'Score de McMahon',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Risque réadmission douleur thoracique',
    color: 'orange',
    fields: [
      { id: 'age', label: 'Âge ≥ 60 ans', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 },
      { id: 'hypertension', label: 'HTA', type: 'checkbox', points: 1 },
      { id: 'cad', label: 'Coronaropathie connue', type: 'checkbox', points: 2 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 1 },
      { id: 'hospitalization', label: 'Hospitalisation cardio < 1 an', type: 'checkbox', points: 1 },
      { id: 'abnormalEcg', label: 'ECG anormal', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age) total += 1;
      if (values.diabetes) total += 1;
      if (values.hypertension) total += 1;
      if (values.cad) total += 2;
      if (values.chf) total += 1;
      if (values.hospitalization) total += 1;
      if (values.abnormalEcg) total += 1;
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `McMahon ${score}`, detail: 'Faible risque de réadmission' };
      if (score <= 4) return { level: 'warning', text: `McMahon ${score}`, detail: 'Risque modéré de réadmission' };
      return { level: 'danger', text: `McMahon ${score}`, detail: 'Risque élevé de réadmission - Hospitalisation recommandée' };
    },
    formula: 'McMahon: identification patients à risque de réadmission'
  },

  // MEWS - Modified Early Warning Score
  mews: {
    name: 'Score MEWS',
    category: 'Réanimation',
    icon: Activity,
    description: 'Détection précoce dégradation clinique',
    color: 'red',
    fields: [
      { id: 'sbp', label: 'PAS (mmHg)', type: 'select', options: [
        { value: 3, label: '≤ 70 (3 pts)' },
        { value: 2, label: '71-80 (2 pts)' },
        { value: 1, label: '81-100 (1 pt)' },
        { value: 0, label: '101-199 (0 pt)' },
        { value: 2, label: '≥ 200 (2 pts)' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque', type: 'select', options: [
        { value: 2, label: '< 40 (2 pts)' },
        { value: 1, label: '40-50 (1 pt)' },
        { value: 0, label: '51-100 (0 pt)' },
        { value: 1, label: '101-110 (1 pt)' },
        { value: 2, label: '111-129 (2 pts)' },
        { value: 3, label: '≥ 130 (3 pts)' }
      ]},
      { id: 'rr', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 2, label: '< 9 (2 pts)' },
        { value: 0, label: '9-14 (0 pt)' },
        { value: 1, label: '15-20 (1 pt)' },
        { value: 2, label: '21-29 (2 pts)' },
        { value: 3, label: '≥ 30 (3 pts)' }
      ]},
      { id: 'temp', label: 'Température (°C)', type: 'select', options: [
        { value: 2, label: '< 35 (2 pts)' },
        { value: 1, label: '35-35.4 (1 pt)' },
        { value: 0, label: '35.5-38.4 (0 pt)' },
        { value: 1, label: '38.5-39 (1 pt)' },
        { value: 2, label: '> 39 (2 pts)' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: 'Alerte (0 pt)' },
        { value: 1, label: 'Répond à la voix (1 pt)' },
        { value: 2, label: 'Répond à la douleur (2 pts)' },
        { value: 3, label: 'Ne répond pas (3 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 14 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `MEWS ${score}`, detail: 'Surveillance standard' };
      if (score <= 4) return { level: 'warning', text: `MEWS ${score}`, detail: 'Surveillance rapprochée - Reerévaluation médicale' };
      return { level: 'danger', text: `MEWS ${score}`, detail: 'Appel médecin urgentiste/réanimateur' };
    },
    formula: 'MEWS = PAS + FC + FR + Temp + Conscience'
  },

  // MIPI - Lymphome du manteau
  mipi: {
    name: 'Score MIPI',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Pronostic lymphome du manteau',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'ecog', label: 'ECOG', type: 'select', options: [
        { value: 0, label: '0-1' },
        { value: 1, label: '2-4' }
      ]},
      { id: 'ldh', label: 'Ratio LDH/LSN', type: 'number', min: 0.1, max: 20, step: 0.1 },
      { id: 'wbc', label: 'Leucocytes (G/L)', type: 'number', min: 0, max: 500 }
    ],
    calculate: (values) => {
      const age = values.age || 60;
      const ecog = values.ecog || 0;
      const ldh = values.ldh || 1;
      const wbc = values.wbc || 7;
      
      // MIPI formula
      const mipi = (0.03535 * age) + (0.6978 * ecog) + (1.367 * Math.log10(ldh)) + (0.9393 * Math.log10(wbc));
      return { score: mipi.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const m = parseFloat(score);
      if (m < 5.7) return { level: 'success', text: `MIPI ${score}`, detail: 'Faible risque - Survie médiane non atteinte' };
      if (m < 6.2) return { level: 'warning', text: `MIPI ${score}`, detail: 'Risque intermédiaire - Survie médiane ~51 mois' };
      return { level: 'danger', text: `MIPI ${score}`, detail: 'Haut risque - Survie médiane ~29 mois' };
    },
    formula: 'MIPI = 0.03535×Age + 0.6978×ECOG + 1.367×log10(LDH) + 0.9393×log10(GB)'
  },

  // Morse - Risque de chute
  morse: {
    name: 'Échelle de Morse',
    category: 'Gériatrie',
    icon: User,
    description: 'Risque de chute hospitalisé',
    color: 'amber',
    fields: [
      { id: 'fallHistory', label: 'ATCD chute (3 mois)', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 25, label: 'Oui (25 pts)' }
      ]},
      { id: 'secondaryDiagnosis', label: 'Diagnostic secondaire', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 15, label: 'Oui (15 pts)' }
      ]},
      { id: 'ambulatoryAid', label: 'Aide à la marche', type: 'select', options: [
        { value: 0, label: 'Aucune/Alitement/Aide soignant (0 pt)' },
        { value: 15, label: 'Canne/Béquilles/Déambulateur (15 pts)' },
        { value: 30, label: 'Se tient aux meubles (30 pts)' }
      ]},
      { id: 'ivTherapy', label: 'Perfusion IV ou catheter', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 20, label: 'Oui (20 pts)' }
      ]},
      { id: 'gait', label: 'Démarche', type: 'select', options: [
        { value: 0, label: 'Normale/Alitement/Fauteuil (0 pt)' },
        { value: 10, label: 'Faible (10 pts)' },
        { value: 20, label: 'Altrérée (20 pts)' }
      ]},
      { id: 'mentalStatus', label: 'État mental', type: 'select', options: [
        { value: 0, label: 'Orienté (0 pt)' },
        { value: 15, label: 'Surévalue ses capacités (15 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 125 };
    },
    interpret: (score) => {
      if (score < 25) return { level: 'success', text: `Morse ${score}`, detail: 'Risque faible - Mesures de base' };
      if (score < 45) return { level: 'warning', text: `Morse ${score}`, detail: 'Risque modéré - Prévention standard' };
      return { level: 'danger', text: `Morse ${score}`, detail: 'Risque élevé - Prévention intensive' };
    },
    formula: 'Morse: somme 6 critères. <25 = faible, 25-45 = modéré, >45 = élevé'
  },

  // MSOFA - Modified SOFA
  msofa: {
    name: 'Score mSOFA',
    category: 'Réanimation',
    icon: Activity,
    description: 'SOFA modifié (sans biologie)',
    color: 'red',
    fields: [
      { id: 'spo2fio2', label: 'SpO₂/FiO₂', type: 'select', options: [
        { value: 0, label: '> 400 (0 pt)' },
        { value: 1, label: '315-400 (1 pt)' },
        { value: 2, label: '235-314 (2 pts)' },
        { value: 3, label: '150-234 (3 pts)' },
        { value: 4, label: '< 150 (4 pts)' }
      ]},
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 0, label: '15 (0 pt)' },
        { value: 1, label: '13-14 (1 pt)' },
        { value: 2, label: '10-12 (2 pts)' },
        { value: 3, label: '6-9 (3 pts)' },
        { value: 4, label: '< 6 (4 pts)' }
      ]},
      { id: 'map', label: 'PAM ou vasopresseurs', type: 'select', options: [
        { value: 0, label: 'PAM ≥ 70 (0 pt)' },
        { value: 1, label: 'PAM < 70 (1 pt)' },
        { value: 2, label: 'Dopamine ≤ 5 ou Dobutamine (2 pts)' },
        { value: 3, label: 'Dopamine > 5 ou NAD/Adr ≤ 0.1 (3 pts)' },
        { value: 4, label: 'NAD/Adr > 0.1 (4 pts)' }
      ]},
      { id: 'creatinine', label: 'Créatinine ou diurèse', type: 'select', options: [
        { value: 0, label: 'Cr < 110 (0 pt)' },
        { value: 1, label: 'Cr 110-170 (1 pt)' },
        { value: 2, label: 'Cr 171-299 (2 pts)' },
        { value: 3, label: 'Cr 300-440 ou diurèse < 500mL/j (3 pts)' },
        { value: 4, label: 'Cr > 440 ou diurèse < 200mL/j (4 pts)' }
      ]},
      { id: 'liver', label: 'Bilirubine', type: 'select', options: [
        { value: 0, label: '< 20 µmol/L (0 pt)' },
        { value: 1, label: '20-32 (1 pt)' },
        { value: 2, label: '33-101 (2 pts)' },
        { value: 3, label: '102-204 (3 pts)' },
        { value: 4, label: '> 204 (4 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `mSOFA ${score}`, detail: 'Faible dysfonction d\'organes' };
      if (score <= 8) return { level: 'warning', text: `mSOFA ${score}`, detail: 'Dysfonction modérée' };
      if (score <= 12) return { level: 'danger', text: `mSOFA ${score}`, detail: 'Dysfonction sévère - Mortalité ~30-50%' };
      return { level: 'danger', text: `mSOFA ${score}`, detail: 'Dysfonction multi-organes - Mortalité > 80%' };
    },
    formula: 'mSOFA = Respiration + Neuro + Cardio + Rénal + Hépatique'
  },

  // MUST - Malnutrition
  must: {
    name: 'Score MUST',
    category: 'Nutrition',
    icon: Scale,
    description: 'Dépistage dénutrition',
    color: 'orange',
    fields: [
      { id: 'bmi', label: 'IMC (kg/m²)', type: 'select', options: [
        { value: 0, label: '> 20 (0 pt)' },
        { value: 1, label: '18.5-20 (1 pt)' },
        { value: 2, label: '< 18.5 (2 pts)' }
      ]},
      { id: 'weightLoss', label: 'Perte de poids involontaire (3-6 mois)', type: 'select', options: [
        { value: 0, label: '< 5% (0 pt)' },
        { value: 1, label: '5-10% (1 pt)' },
        { value: 2, label: '> 10% (2 pts)' }
      ]},
      { id: 'acuteDisease', label: 'Maladie aiguë avec arrêt alimentaire > 5j', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 2, label: 'Oui (2 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `MUST ${score}`, detail: 'Faible risque - Réévaluation hebdomadaire' };
      if (score === 1) return { level: 'warning', text: `MUST ${score}`, detail: 'Risque moyen - Documenter apports, avis diététique' };
      return { level: 'danger', text: `MUST ${score}`, detail: 'Risque élevé - Prise en charge nutritionnelle' };
    },
    formula: 'MUST = IMC + Perte poids + Maladie aiguë'
  },

  // Naranjo - Imputabilité médicamenteuse
  naranjo: {
    name: 'Score de Naranjo',
    category: 'Pharmacovigilance',
    icon: Pill,
    description: 'Imputabilité effet indésirable médicamenteux',
    color: 'blue',
    fields: [
      { id: 'previousReports', label: 'Rapports antérieurs concluants?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]},
      { id: 'afterDrug', label: 'EI apparu après administration?', type: 'select', options: [
        { value: 2, label: 'Oui (+2)' }, { value: -1, label: 'Non (-1)' }, { value: 0, label: 'NSP (0)' }
      ]},
      { id: 'withdrawal', label: 'Amélioration à l\'arrêt?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]},
      { id: 'rechallenge', label: 'Réapparition à la réintroduction?', type: 'select', options: [
        { value: 2, label: 'Oui (+2)' }, { value: -1, label: 'Non (-1)' }, { value: 0, label: 'NSP (0)' }
      ]},
      { id: 'alternativeCause', label: 'Autres causes possibles?', type: 'select', options: [
        { value: -1, label: 'Oui (-1)' }, { value: 2, label: 'Non (+2)' }, { value: 0, label: 'NSP (0)' }
      ]},
      { id: 'placebo', label: 'Réaction avec placebo?', type: 'select', options: [
        { value: -1, label: 'Oui (-1)' }, { value: 1, label: 'Non (+1)' }, { value: 0, label: 'NSP (0)' }
      ]},
      { id: 'drugConcentration', label: 'Concentration sanguine toxique?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]},
      { id: 'doseRelated', label: 'Dose-dépendance?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]},
      { id: 'previousExposure', label: 'Réaction similaire précédente?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]},
      { id: 'objectiveEvidence', label: 'Confirmation objective?', type: 'select', options: [
        { value: 1, label: 'Oui (+1)' }, { value: 0, label: 'Non/NSP (0)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 13, min: -4 };
    },
    interpret: (score) => {
      if (score >= 9) return { level: 'danger', text: `Naranjo ${score}`, detail: 'Imputabilité certaine' };
      if (score >= 5) return { level: 'warning', text: `Naranjo ${score}`, detail: 'Imputabilité probable' };
      if (score >= 1) return { level: 'warning', text: `Naranjo ${score}`, detail: 'Imputabilité possible' };
      return { level: 'success', text: `Naranjo ${score}`, detail: 'Imputabilité douteuse' };
    },
    formula: 'Naranjo: ≥ 9 certain, 5-8 probable, 1-4 possible, ≤ 0 douteux'
  }
};
